import { Dayjs } from 'dayjs';
import type { GtinMarkItem } from '../../api/marking';

// Единые типы черновика заказ-наряда — общие для полного вида (ServiceOrderPage)
// и вида «Замена масла» (OilChangePage). Схема: ServiceOrderPage.md §2В (rev. 2026-09-08).

export type ItemType = 'service' | 'part';

export interface DraftLine {
  key: string;
  external_1c_id: string | null;
  name: string;
  quantity: number;
  unit: string;
  price: number;
  recommended_price?: number;
  // Цена из клиентского прайса — в виде «Замена масла» цена масла read-only
  // (OilChangePage.md rev. 2026-09-12 №19)
  price_from_price_list?: boolean;
  sum?: number | null;
  // Исполнители (для услуг)
  executor1_id?: string | null;
  executor1_name?: string | null;
  executor2_id?: string | null;
  executor2_name?: string | null;
  executor3_id?: string | null;
  executor3_name?: string | null;
  // НДС
  vat?: number | null;
  vat_rate?: string | null;
  sum_with_vat?: number | null;
  // Для услуг
  norm_hour?: number | null;
  work_code?: string | null;
  // Маркировка
  marking_code?: string | null;
  // Марка без криптохвоста в реестре (rev. 2026-09-17 №7): false → красный текст марки
  mark_has_full_code?: boolean | null;
  // Вычисляемый статус марки в чеке ККМ 1С (ok | mismatch | missing); null/нет — не из чека
  mark_status?: 'ok' | 'mismatch' | 'missing' | null;
  // Отложенная позиция режима «без оприходования» (unreceived_sales.md v3): марки нет
  // в ШУТ 1С, позиция хранится локально; оранжевый тег «ожидает оприходования»
  unreceived_pending?: boolean;
  // Разливной товар (rev. 2026-08-10): мастер-строка + дочерние марки
  is_bulk_master?: boolean;          // мастер-строка (товар, объём = сумма дочерних)
  // Штучный маркированный товар (rev. 2026-08-14): мастер + дочерние марки, 1 марка = 1 шт
  is_piece_master?: boolean;         // мастер-строка (количество = число дочерних марок, не редактируется)
  bulk_gtin?: string | null;         // GTIN товара (для окна/подбора)
  available_marks?: GtinMarkItem[] | null;  // марки GTIN с сервера (порядок подбора)
  children?: DraftLine[];            // дочерние строки марок (только у мастера)
  parent_key?: string;               // ключ мастера (только у дочерней)
  manual_volume?: boolean;           // дочерняя: включена экстренная правка объёма
  available_volume?: number | null;  // дочерняя: свободный остаток марки (без нашего резерва)
}

export interface Draft {
  vehicle_id: string | null;
  vehicle: string | null;
  // Марка/модель из справочника AR_DB (vehicle_resolver.md); null — авто из 1С как есть
  vehicle_make: string | null;
  vehicle_model: string | null;
  license_plate: string | null;
  vin: string | null;
  year: number | null;
  color: string | null;
  engine_type: string | null;
  engine_number: string | null;
  body_number: string | null;
  client_type: 'individual' | 'counterparty' | null;
  client_id: number | null;
  client_label: string | null;
  retail_buyer_name: string | null;
  mileage: number | null;
  notes: string;
  prepayment_sum: number | null;
  work_start_date: Dayjs | null;
  work_end_date: Dayjs | null;
  services: DraftLine[];
  parts: DraftLine[];
  payment_type: 'Касса' | 'Банк';
  card_payment: boolean;
  print_receipt: boolean;
  vat_for_products: boolean;
  vat_for_services: boolean;
  bank_terminal: string | null;
  org_bank_account_id: string | null;
}

export const newLine = (executors?: Partial<Pick<DraftLine, 'executor1_id' | 'executor1_name' | 'executor2_id' | 'executor2_name' | 'executor3_id' | 'executor3_name'>>): DraftLine => ({
  key: Math.random().toString(36).slice(2),
  external_1c_id: null,
  name: '', quantity: 1, unit: 'шт', price: 0, recommended_price: 0,
  executor1_id: executors?.executor1_id ?? null,
  executor1_name: executors?.executor1_name ?? null,
  executor2_id: executors?.executor2_id ?? null,
  executor2_name: executors?.executor2_name ?? null,
  executor3_id: executors?.executor3_id ?? null,
  executor3_name: executors?.executor3_name ?? null,
});

export const isEmptyLine = (l: DraftLine): boolean =>
  !l.name.trim() && !l.external_1c_id;

export const ensureTrailingEmpty = (lines: DraftLine[]): DraftLine[] => {
  const nonEmpty = lines.filter(l => !isEmptyLine(l));
  return [...nonEmpty, newLine()];
};

// Инфо-подстрока мастера разлива (rev. 2026-09-12 №23): основная строка товара
// становится «двустрочной» — под ней ряд с «Доступно: X л» (колонка «Кол-во»)
// и «по прайсу: X ₽» (колонка «Цена», только при расхождении, вид «Замена масла»).
// Только для рендера: подставляется ПЕРВЫМ ребёнком мастера в dataSource (в draft
// не входит) — antd рисует детей сразу под мастером, до строк-марок, поэтому
// подстрока не «уезжает» под последнюю марку (проблема бывшей ::avail, rev. №7).
export const INFO_ROW_SUFFIX = '::info';

export const isInfoRow = (l: DraftLine): boolean => l.key.endsWith(INFO_ROW_SUFFIX);

export const withBulkInfoRow = (l: DraftLine): DraftLine =>
  l.is_bulk_master
    ? {
        ...l,
        children: [
          { ...l, key: l.key + INFO_ROW_SUFFIX, parent_key: l.key, children: undefined, marking_code: null },
          ...(l.children || []),
        ],
      }
    : l;

export const isPieceUnit = (unit?: string | null): boolean => {
  if (unit) {
    const u = unit.trim().toLowerCase();
    if (u === 'шт' || u === 'шт.' || u === 'pcs' || u === 'уп' || u === 'уп.' || u === 'бут' || u === 'бут.' || u === 'пар' || u === 'пар.' || u === 'компл' || u === 'компл.' || u === 'пач' || u === 'пач.' || u === 'к-т' || u === 'к-т.' || u === 'ед.') return true;
    return false;
  }
  return true;
};

export const isVolumeUnit = (unit?: string | null): boolean => {
  if (unit) {
    const u = unit.trim().toLowerCase();
    if (u === 'л' || u === 'дм3' || u === 'дм³' || u.includes('л') || u.includes('дм3')) return true;
    return false;
  }
  return false;
};

// Отображаемая единица: «л (дм3)», «Л», «дм3» → «л»; прочие — как есть
export const displayUnit = (unit?: string | null): string => {
  const u = (unit || '').trim();
  if (!u) return '';
  const l = u.toLowerCase();
  if (l.startsWith('л') || l === 'дм3' || l === 'дм³') return 'л';
  return u;
};

// Объём в литрах с запятой-разделителем (rev. 2026-09-26 №18/№46): 4.5 → «4,5», 53 → «53»
export const fmtLiters = (v: number): string => String(v).replace('.', ',');

// Объём с фиксированным знаком после запятой (rev. 2026-09-26 №18/№46): 208 → «208,0»
export const fmtLitersFixed = (v: number, digits = 1): string => v.toFixed(digits).replace('.', ',');
